// ONE WAY TO OPEN A PROJECT FROM THE STUDIO (lane FU2c, 2026-10-31 —
// concierge 13:06: "one open function shared by the tile and the menu row").
// Every "open the project" entry opens a project HERE, so each one lands
// where the /studio tile's double-click (and Enter) lands:
//   an AI-built app (create_app / create_extension) → its rendered preview in
//     /add-app. It renders from SOURCE: the website editor has nothing to
//     show for it (docs/APP_SPLIT.md — a workspace app never opens there);
//   every other project → its route (projectHref: the workspace-scoped form
//     inside the owner's org, else /<owner>/<key>).
// Callers: the tile and the project menu's "Open in app" (pages/studios.js),
// the Studio card's "Open canvas" (pages/dashboard.js), the dashboard rows
// (components/DashboardRows.js) and the AI answer card's "Open canvas"
// (functions/canvasAnswer.js). The rows whose job is the EDITOR itself (the
// env-pinned open, Publish, Version history) keep their own route.
//
// The /add-app hand-off is the shell's window bridge exactly as the tile has
// always used it: `window.__addAppOpenId` (read once by the /add-app page,
// consumeAddAppOpenId) and `window.__wsRoot.call('openAppPage', '/add-app')`.
// Outside the shell (no `window.__wsRoot`), or when it throws, the project
// route is the fallback. FA204: each export is self-contained; siblings
// through this.call.

// el.call('isStudioAiBuiltApp', project, aiAppIds) — is the project an
// AI-built app? It has a db `_id` (the /add-app hand-off selects the app by
// it), and that `_id` is in `aiAppIds` (the caller's own list, a Set or an
// array: the /studio board passes the ids it reads from ai.listExtensions on
// each load), or in the workspace's app-split signals
// (projectKindContext(root).extensions, the shared classifier's list), or the
// board said so when the project was selected (`aiBuilt: true` on
// root._studioSelected — the Studio card cannot read the board's list). The
// Studio card's label reads this same answer, so its words and its open agree.
export function isStudioAiBuiltApp(project, aiAppIds) {
  const el = this
  const p = project && typeof project === 'object' ? project : {}
  const id = p._id ? String(p._id) : ''
  if (!id) return false
  if (p.aiBuilt === true) return true
  if (aiAppIds && typeof aiAppIds.has === 'function' && aiAppIds.has(id)) return true
  if (Array.isArray(aiAppIds) && aiAppIds.some((x) => String(x) === id)) return true
  if (!el || typeof el.call !== 'function') return false
  let root = (el.state && el.state.root) || null
  if (!root && typeof el.getRoot === 'function') {
    const rootEl = el.getRoot()
    root = (rootEl && rootEl.state) || null
  }
  let ctx = null
  try {
    ctx = el.call('projectKindContext', root)
  } catch (_) {
    ctx = null
  }
  const want = id.trim().toLowerCase()
  return !!(
    ctx &&
    Array.isArray(ctx.extensions) &&
    ctx.extensions.some((x) => String(x).trim().toLowerCase() === want)
  )
}

// el.call('studioOpenProject', project, aiAppIds) — open the project (a board
// node, the Studio card's selection, an SDK row or an answer card row: its
// `owner`, `key` and `_id`) the way the /studio tile does. `aiAppIds` as in
// isStudioAiBuiltApp. true when it opened something.
export function studioOpenProject(project, aiAppIds) {
  const el = this
  const p = project && typeof project === 'object' ? project : {}
  if (!el || typeof el.call !== 'function' || !p.owner || !p.key) return false
  const id = p._id ? String(p._id) : ''
  if (
    id &&
    el.call('isStudioAiBuiltApp', p, aiAppIds) === true &&
    typeof window !== 'undefined' &&
    window.__wsRoot &&
    typeof window.__wsRoot.call === 'function'
  ) {
    try {
      window.__addAppOpenId = id
      window.__wsRoot.call('openAppPage', '/add-app')
      return true
    } catch (_) {
      /* fall through to the project route */
    }
  }
  const urls = el.call('buildProjectUrls', p.owner, p.key)
  if (!urls) return false
  const rootEl = typeof el.getRoot === 'function' ? el.getRoot() : null
  const root = (el.state && el.state.root) || (rootEl && rootEl.state) || null
  el.router(el.call('projectHref', root, urls.safeOwner, urls.safeKey), rootEl)
  return true
}
